import { TrendingUp, Clock, Users, Sparkles, Star, Home } from "lucide-react";
import Reveal from "@/components/common/Reveal";
import neuroFastheal from "@/assets/recent-work/neuro-fastheal-hero.png";

const cases = [
  {
    industry: "Healthcare / Physiotherapy",
    title: "Neuro Fastheal — home-visit physiotherapy brand & booking experience.",
    summary:
      "Designed and built a clean, high-conversion landing page for Dr. Gaurav Kumar PT, featuring online appointment booking, WhatsApp consultation, social proof and a modern medical aesthetic.",
    metrics: [
      { icon: Users, label: "Patients treated", value: "164+" },
      { icon: Star, label: "Google rating", value: "4.9/5" },
      { icon: Home, label: "Care model", value: "Home visit" },
    ],
    tag: "Recent work",
    image: neuroFastheal,
  },
  {
    industry: "Real Estate",
    title: "From 200 cold leads to 42 site visits — in 30 days.",
    summary:
      "Deployed a WhatsApp AI assistant + CRM that qualifies, books and reminds prospects automatically. Sales team only touched the warmest deals.",
    metrics: [
      { icon: TrendingUp, label: "Site visits", value: "+62%" },
      { icon: Clock, label: "Avg. response", value: "8 sec" },
      { icon: Users, label: "Manual replies", value: "-85%" },
    ],
    tag: "Sample case study",
  },
  {
    industry: "Clinic",
    title: "Slashing no-shows for a 3-location dental clinic.",
    summary:
      "AI receptionist on WhatsApp + smart reminders, rescheduling and post-visit review collection running across all 3 branches.",
    metrics: [
      { icon: TrendingUp, label: "5-star reviews", value: "+3×" },
      { icon: Clock, label: "No-shows", value: "-48%" },
      { icon: Sparkles, label: "Front-desk load", value: "-60%" },
    ],
    tag: "Sample case study",
  },
  {
    industry: "Restaurant",
    title: "Tripling direct orders without a single ad rupee.",
    summary:
      "WhatsApp ordering, loyalty re-engagement and review automation funnel for a 5-outlet QSR brand.",
    metrics: [
      { icon: TrendingUp, label: "Direct orders", value: "+2.4×" },
      { icon: Users, label: "Returning diners", value: "+38%" },
      { icon: Clock, label: "Order time", value: "-43%" },
    ],
    tag: "Sample case study",
  },
];

function MetricPill({ metric }) {
  const Icon = metric.icon;
  return (
    <div className="flex items-center gap-2 rounded-full border border-[var(--glass-border)] bg-[var(--eyebrow-bg)] px-3 py-1.5">
      <Icon className="w-3.5 h-3.5 text-[var(--brand)]" />
      <span className="text-sm text-[var(--body-fg)] font-medium">{metric.value}</span>
      <span className="text-[10px] text-[var(--body-fg)]/45 uppercase tracking-widest hidden sm:inline">
        {metric.label}
      </span>
    </div>
  );
}

function MetricTile({ metric }) {
  const Icon = metric.icon;
  return (
    <div className="rounded-2xl border border-[var(--glass-border)] bg-[var(--eyebrow-bg)] p-4 text-center tilt-card">
      <Icon className="w-4 h-4 mx-auto text-[var(--brand)]" />
      <div className="mt-2 text-xl text-[var(--body-fg)] font-semibold tracking-tight">{metric.value}</div>
      <div className="text-[10px] text-[var(--body-fg)]/45 mt-1 uppercase tracking-widest">{metric.label}</div>
    </div>
  );
}

export default function CaseStudies() {
  return (
    <section id="case-studies" className="section relative" data-testid="case-studies-section">
      <div className="container-x relative">
        <div className="text-center max-w-3xl mx-auto">
          <Reveal direction="up">
            <span className="eyebrow">
              <span className="dot" /> Case Studies
            </span>
          </Reveal>
          <Reveal direction="up" delay={0.05}>
            <h2 className="h-display mt-5 text-4xl sm:text-5xl lg:text-6xl text-[var(--body-fg)]">
              Outcomes, not <span className="font-serif italic gradient-text">dashboards</span>.
            </h2>
          </Reveal>
          <Reveal direction="up" delay={0.1}>
            <p className="mt-5 text-[var(--body-fg)]/55 text-sm">
              * Recent work alongside sample scenarios shown for illustration.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 space-y-6">
          {cases.map((c, i) => (
            <Reveal key={c.title} direction="up" delay={i * 0.08}>
              <article
                className="relative glass rounded-3xl p-6 sm:p-8 tilt-card glow-border overflow-hidden"
                data-testid={`case-study-${i}`}
              >
                <div
                  className="absolute -top-24 -right-24 w-72 h-72 rounded-full blur-3xl opacity-60 pointer-events-none"
                  style={{ background: "radial-gradient(circle, var(--brand-glow), transparent 60%)" }}
                />
                <div className="relative grid lg:grid-cols-12 gap-8 items-center">
                  {c.image ? (
                    <>
                      <div className="lg:col-span-7 order-2 lg:order-1">
                        <div className="flex items-center gap-3">
                          <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--brand)]">
                            {c.tag}
                          </span>
                          <span className="text-xs px-2 py-0.5 rounded-full border border-[var(--glass-border)] bg-[var(--eyebrow-bg)] text-[var(--body-fg)]/70">
                            {c.industry}
                          </span>
                        </div>
                        <h3 className="mt-4 text-2xl sm:text-3xl text-[var(--body-fg)] font-semibold tracking-tight leading-snug">
                          {c.title}
                        </h3>
                        <p className="mt-4 text-[var(--body-fg)]/65 leading-relaxed">{c.summary}</p>
                        <div className="mt-6 flex flex-wrap gap-3">
                          {c.metrics.map((m) => (
                            <MetricPill key={m.label} metric={m} />
                          ))}
                        </div>
                      </div>
                      <div className="lg:col-span-5 order-1 lg:order-2">
                        <div className="group rounded-2xl overflow-hidden border border-[var(--glass-border)] bg-[var(--body-fg)]/[0.03] relative">
                          <img
                            src={c.image}
                            alt={c.title}
                            loading="lazy"
                            className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-105"
                          />
                          <div
                            className="pointer-events-none absolute inset-0"
                            style={{ boxShadow: "inset 0 0 60px -10px var(--brand-glow)" }}
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="lg:col-span-7">
                        <div className="flex items-center gap-3">
                          <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--brand)]">
                            {c.tag}
                          </span>
                          <span className="text-xs px-2 py-0.5 rounded-full border border-[var(--glass-border)] bg-[var(--eyebrow-bg)] text-[var(--body-fg)]/70">
                            {c.industry}
                          </span>
                        </div>
                        <h3 className="mt-4 text-2xl sm:text-3xl text-[var(--body-fg)] font-semibold tracking-tight leading-snug">
                          {c.title}
                        </h3>
                        <p className="mt-4 text-[var(--body-fg)]/65 leading-relaxed">{c.summary}</p>
                      </div>
                      <div className="lg:col-span-5 grid grid-cols-3 gap-3">
                        {c.metrics.map((m) => (
                          <MetricTile key={m.label} metric={m} />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
